import { Button, IconButton } from "@ui-lib/index"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { MdClose } from "react-icons/md"
import Joyride, {
	ACTIONS,
	CallBackProps,
	EVENTS,
	STATUS,
	Step,
	TooltipRenderProps,
} from "react-joyride"
import { cbmSteps } from "./moduleSteps/CBMManual"
import { consumptionSteps } from "./moduleSteps/ConsumptionManual"
import { downloadDataSteps } from "./moduleSteps/DownloadDataManual"
import { efficiencySteps } from "./moduleSteps/EfficiencyManual"
import { fleetOverviewSteps } from "./moduleSteps/FleetOverviewManual"
import { historicalDataSteps } from "./moduleSteps/HistoricalDataManual"
import { operationalEventSteps, operationsSteps } from "./moduleSteps/OperationsManual"
import { vesselStatusSteps } from "./moduleSteps/VesselStatusManual"

interface UserManualProps {
	moduleName: string
	onStartTour?: (startFn: () => void) => void
	stepsFromOutside?: Step[]
	scrollRules?: {
		target: string
		opts: ScrollRule
	}[]
	disableScrolling?: boolean
	onStateChange?: (state: UserManualState) => void
}

const moduleStepsMap: Record<string, Step[]> = {
	consumption: consumptionSteps(),
	cbm: cbmSteps(),
	downloadData: downloadDataSteps(),
	efficiency: efficiencySteps(),
	historicalData: historicalDataSteps(),
	operations: operationsSteps(),
	operationalEvent: operationalEventSteps(),
	fleetOverview: fleetOverviewSteps(),
	vesselStatus: vesselStatusSteps(),
}

type Align = "start" | "center" | "end"
type AxisRule = { align?: Align; offset?: number }
export type ScrollRule = {
	behavior?: ScrollBehavior
	containerSelector?: string // optional: scroll inside container
	x?: AxisRule
	y?: AxisRule
	skip?: boolean
}

export type UserManualState = {
	run: boolean
	stepIndex: number
	step?: Step
	close: () => void
}

function clamp(n: number, min: number, max: number) {
	return Math.max(min, Math.min(max, n))
}
function scrollToTarget(target: string, opts?: ScrollRule) {
	const el = document.querySelector<HTMLElement>(target)
	if (el === null) return

	const options: ScrollRule = opts ?? {
		y: { offset: 0, align: "start" },
		behavior: "smooth",
		// containerSelector,
		skip: false,
	}
	if (options.behavior === undefined) options.behavior = "smooth"
	if (options.skip === true) return

	const container =
		options.containerSelector !== undefined
			? document.querySelector<HTMLElement>(options.containerSelector)
			: null

	const rect = el.getBoundingClientRect()

	if (container === null) {
		const maxX = document.documentElement.scrollWidth - window.innerWidth
		const maxY = document.documentElement.scrollHeight - window.innerHeight

		let nextX = window.scrollX
		let nextY = window.scrollY

		if (options?.y !== undefined) {
			const align = options.y.align ?? "start"
			const offset = options.y.offset ?? 0

			const viewportY =
				align === "center"
					? rect.top - window.innerHeight / 2 + rect.height / 2
					: align === "end"
						? rect.top - window.innerHeight + rect.height
						: rect.top

			nextY = clamp(window.scrollY + viewportY - offset, 0, Math.max(0, maxY))
		}

		if (options?.x !== undefined) {
			const align = options.x.align ?? "start"
			const offset = options.x.offset ?? 0

			const viewportX =
				align === "center"
					? rect.left - window.innerWidth / 2 + rect.width / 2
					: align === "end"
						? rect.left - window.innerWidth + rect.width
						: rect.left

			nextX = clamp(window.scrollX + viewportX - offset, 0, Math.max(0, maxX))
		}

		window.scrollTo({ top: nextY, left: nextX, behavior: options.behavior })
		return
	}

	const cRect = container.getBoundingClientRect()

	const elTopInContainer = rect.top - cRect.top + container.scrollTop
	const elLeftInContainer = rect.left - cRect.left + container.scrollLeft

	let nextTop = container.scrollTop
	let nextLeft = container.scrollLeft

	if (options?.y !== undefined) {
		const align = options.y.align ?? "start"
		const offset = options.y.offset ?? 0

		const targetTop =
			align === "center"
				? elTopInContainer - container.clientHeight / 2 + rect.height / 2
				: align === "end"
					? elTopInContainer - container.clientHeight + rect.height
					: elTopInContainer

		nextTop = clamp(
			targetTop - offset,
			0,
			Math.max(0, container.scrollHeight - container.clientHeight)
		)
	}

	if (options?.x !== undefined) {
		const align = options.x.align ?? "start"
		const offset = options.x.offset ?? 0

		const targetLeft =
			align === "center"
				? elLeftInContainer - container.clientWidth / 2 + rect.width / 2
				: align === "end"
					? elLeftInContainer - container.clientWidth + rect.width
					: elLeftInContainer

		nextLeft = clamp(
			targetLeft - offset,
			0,
			Math.max(0, container.scrollWidth - container.clientWidth)
		)
	}
	container.scrollTo({ top: nextTop, left: nextLeft, behavior: options.behavior })
}

export const UserManual = ({
	moduleName,
	onStartTour,
	stepsFromOutside,
	scrollRules,
	disableScrolling = false,
	onStateChange,
}: UserManualProps) => {
	const { t, i18n } = useTranslation()

	const [run, setRun] = useState(false)
	const [stepIndex, setStepIndex] = useState(0)
	const [retryTick, setRetryTick] = useState(0)
	const lastReportedManualStateRef = useRef<string | null>(null)
	const scrollRetryTimeoutRef = useRef<number | null>(null)
	const scrollRetryAttemptsRef = useRef<Record<string, number>>({})

	const clearScrollRetry = useCallback(() => {
		if (scrollRetryTimeoutRef.current !== null) {
			window.clearTimeout(scrollRetryTimeoutRef.current)
			scrollRetryTimeoutRef.current = null
		}
	}, [])

	useEffect(() => {
		return clearScrollRetry
	}, [clearScrollRetry])

	const closeTour = useCallback(() => {
		clearScrollRetry()
		scrollRetryAttemptsRef.current = {}
		setRun(false)
		setStepIndex(0)
		const storageKey = `userManual_visited_${moduleName}`
		localStorage.setItem(storageKey, "true")
	}, [clearScrollRetry, moduleName])

	const steps = useMemo(() => {
		return stepsFromOutside ?? moduleStepsMap[moduleName] ?? []
	}, [moduleName, stepsFromOutside])

	useEffect(() => {
		const storageKey = `userManual_visited_${moduleName}`
		const hasVisited = localStorage.getItem(storageKey)

		if (hasVisited !== "true" && steps.length > 0) {
			setRun(true) // Auto-start the tour on first visit or if manually reset
		}
	}, [moduleName, steps.length])

	const handleJoyrideCallback = useCallback(
		(data: CallBackProps) => {
			const { status, action, index, type, step } = data
			const target = step.target !== undefined ? (step.target as string) : undefined
			const matchingScrollRule =
				target !== undefined
					? scrollRules?.find((r) => r.target === target)?.opts
					: undefined

			if (type === EVENTS.STEP_BEFORE && target !== undefined && scrollRules !== undefined) {
				clearScrollRetry()
				scrollRetryAttemptsRef.current[target] = 0
				scrollToTarget(target, matchingScrollRule)
			}
			const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED]

			if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
				closeTour()
			} else if (type === "step:after") {
				setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1))
			} else if (type === "error:target_not_found") {
				if (target !== undefined && matchingScrollRule !== undefined) {
					const attempts = scrollRetryAttemptsRef.current[target] ?? 0
					if (attempts < 8) {
						scrollRetryAttemptsRef.current[target] = attempts + 1
						clearScrollRetry()
						scrollRetryTimeoutRef.current = window.setTimeout(() => {
							scrollRetryTimeoutRef.current = null
							setRetryTick((prev) => prev + 1)
							setStepIndex(index)
						}, 200)
						return
					}
				}

				// Skip to next step if target not found, or end tour if it's the last step
				if (index + 1 >= steps.length) {
					closeTour()
				} else {
					setStepIndex(index + 1)
				}
			}
		},
		[clearScrollRetry, closeTour, scrollRules, steps.length]
	)

	const handleStartTour = useCallback(() => {
		setStepIndex(0)
		setRun(true)
	}, [])

	useEffect(() => {
		if (onStartTour !== undefined) {
			onStartTour(handleStartTour)
		}
	}, [onStartTour, handleStartTour])

	useEffect(() => {
		const currentStep = steps[stepIndex]
		const stateSignature = `${run}:${stepIndex}:${String(currentStep?.target ?? "")}`
		if (lastReportedManualStateRef.current === stateSignature) {
			return
		}

		lastReportedManualStateRef.current = stateSignature
		onStateChange?.({
			run,
			stepIndex,
			step: currentStep,
			close: closeTour,
		})
	}, [closeTour, onStateChange, run, stepIndex, steps])

	return (
		<Joyride
			hideCloseButton={true}
			steps={steps}
			run={run}
			stepIndex={stepIndex}
			continuous
			tooltipComponent={CustomTooltip}
			showProgress
			showSkipButton
			callback={handleJoyrideCallback}
			disableCloseOnEsc={false}
			disableOverlayClose={false}
			spotlightClicks={false}
			disableScrollParentFix={true}
			disableScrolling={disableScrolling}
			scrollToFirstStep={!disableScrolling}
			styles={{
				overlay: { maxHeight: "100vh", maxWidth: "100vw" },
				options: {
					primaryColor: "#2b4861",
					overlayColor: "rgba(0,0,0,0.7)",
					zIndex: 10000,
				},
			}}
			locale={{
				back: t("joyride.back"),
				last: t("joyride.last"),
				next: t("joyride.next"),
				skip: t("joyride.skip"),
				nextLabelWithProgress: t("joyride.nextLabelWithProgress"),
			}}
			key={`${i18n.resolvedLanguage}:${retryTick}`}
		/>
	)
}

function CustomTooltip(props: TooltipRenderProps) {
	const {
		backProps,
		closeProps,
		continuous,
		index,
		primaryProps,
		skipProps,
		step,
		tooltipProps,
	} = props

	const btnRef = useRef<HTMLButtonElement | null>(null)

	useEffect(() => {
		const id = window.setTimeout(() => {
			btnRef.current?.focus() //{ preventScroll: true })
		}, 100)

		return () => window.clearTimeout(id)
	}, [index, step?.target])

	return (
		<div
			className="max-w-[100vw] min-w-fit rounded-sm bg-white p-4"
			{...tooltipProps}
		>
			{step.hideCloseButton !== true ? (
				<div className={`flex w-full justify-end`}>
					<IconButton
						variant="text-dark"
						{...closeProps}
					>
						<IconButton.Icon>{MdClose}</IconButton.Icon>
					</IconButton>
				</div>
			) : null}
			{step.title !== undefined ? <h4 className="">{step.title}</h4> : null}
			<div className="flex flex-col gap-1">{step.content}</div>
			<div className="mt-3 flex flex-row justify-between gap-8">
				<Button
					variant="outline-dark"
					{...skipProps}
				>
					<Button.Label>{skipProps.title}</Button.Label>
				</Button>
				<div className="flex flex-row gap-4">
					{index > 0 ? (
						<Button
							variant="outline-dark"
							{...backProps}
						>
							<Button.Label>{backProps.title}</Button.Label>
						</Button>
					) : null}
					{continuous === true ? (
						<Button
							variant="filled-secondary"
							// autoFocus={index === 0}
							{...primaryProps}
							ref={(el) => {
								btnRef.current = el
							}}
						>
							<Button.Label>{primaryProps.title}</Button.Label>
						</Button>
					) : null}
				</div>
			</div>
		</div>
	)
}
